import request, { Response } from "superagent";

export interface XHRApiAuth {
    headers: [string, string][];
    cookies: [string, string][];
    baseUrl: string;
    verifier: () => void;
}

function _applyApiAuth(req, apiAuth: XHRApiAuth) {
    apiAuth.verifier(); // throws if problem

    apiAuth.headers.forEach(function ([name, value]) {
        req.set(name, value);
    });

    if (apiAuth.cookies.length > 0) {
        const cookieHeaderValue = apiAuth.cookies
            .map(function ([name, value]) {
                return name + "=" + encodeURIComponent(value);
            })
            .join(";");
        req.set("Cookie", cookieHeaderValue);
    }
}

export function makeRequest(
    apiAuth: XHRApiAuth,
    method: "GET" | "POST",
    url: string,
    data?: any,
): Promise<Response> {
    const fullUrl = apiAuth.baseUrl + url;
    const req = request(method, fullUrl);
    req.withCredentials();
    req.set("X-Requested-With", "XMLHttpRequest");

    try {
        _applyApiAuth(req, apiAuth);
    } catch (e) {
        return Promise.reject(e);
    }

    if (data) {
        if (method === "GET") {
            req.query(data);
        } else {
            req.send(data);
        }
    }

    return req.catch((err) => {
        // fluxible-router is checking "statusCode" instead of "status"
        err.statusCode = err.status;
        throw err;
    });
}

const DEFAULT_NUMBER_OF_RETRIES = 1;

/**
 * Try to make request - if it fails with crossDomain error, retry it
 *
 * @param apiAuth
 * @param method {string}
 * @param url {string}
 * @param data {object}
 * @param repeatable {int|bool|undefined} Can we retry request?
 *      if undefined - DEFAULT_NUMBER_OF_RETRIES will be used when method === 'GET'
 *      if true - DEFAULT_NUMBER_OF_RETRIES will be used
 *      if int - number of retries
 */
export function makeRequestWithRetries(
    apiAuth: XHRApiAuth,
    method: "GET" | "POST",
    url: string,
    data?: any,
    repeatable?: number | boolean,
): Promise<Response> {
    let numOfRetries = 0;

    if (
        (repeatable === undefined && method === "GET") || // auto-detect
        repeatable === true
    ) {
        // use default
        numOfRetries = DEFAULT_NUMBER_OF_RETRIES;
    }

    let promise = makeRequest(apiAuth, method, url, data);
    for (let i = 0; i < numOfRetries; i++) {
        promise = promise.catch((err) => {
            if (err.crossDomain) {
                // Retry request
                return makeRequest(apiAuth, method, url, data);
            }

            throw err; // Re-throw
        });
    }
    return promise;
}
